// src/components/LegalPage.jsx
// Handles all legal document routes: /terms /privacy /eula /security /data-choices
import { TERMS_VERSION, TERMS_EFFECTIVE_DATE } from "../legalVersion";

const COMPANY = "Darn Good Home Inspections LLC";
const CONTACT_EMAIL = "legal@darngoodhomeinspections.com"; // ← update before launch
const CONTACT_STATE = "California";

// ── Shared shell ──────────────────────────────────────────────────────────────
function LegalShell({ title, version, effectiveDate, children }) {
  return (
    <div className="legal-page">
      <header className="legal-header">
        <a href="/" className="legal-logo">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18, verticalAlign: "middle", marginRight: 6 }}>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          Pinch Point
        </a>
        <a href="/" className="legal-back-link">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to App
        </a>
      </header>

      <div className="legal-body">
        <h1 className="legal-doc-title">{title}</h1>
        <div className="legal-meta">
          <span className="legal-meta-item"><strong>Version</strong> {version}</span>
          <span className="legal-meta-item"><strong>Effective</strong> {effectiveDate}</span>
          <span className="legal-meta-item"><strong>Owner</strong> {COMPANY}</span>
        </div>
        {children}
      </div>

      <footer className="legal-page-footer">
        <p>© {new Date().getFullYear()} {COMPANY} · All rights reserved.</p>
        <p style={{ marginTop: 6, display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
          <a href="/terms" className="legal-page-footer-link">Terms of Service</a>
          <a href="/privacy" className="legal-page-footer-link">Privacy Policy</a>
          <a href="/eula" className="legal-page-footer-link">EULA</a>
          <a href="/security" className="legal-page-footer-link">Data Security</a>
          <a href="/data-choices" className="legal-page-footer-link">Data Choices</a>
        </p>
      </footer>
    </div>
  );
}

// ── Terms of Service ───────────────────────────────────────────────────────────
function TermsContent() {
  return (
    <LegalShell title="Terms of Service" version={TERMS_VERSION} effectiveDate={TERMS_EFFECTIVE_DATE}>
      <div className="legal-toc">
        <div className="legal-toc-title">Table of Contents</div>
        <ol>
          {["Acceptance","Description of Service","License Grant","Proprietary Rights & IP",
            "Prohibited Conduct","User Content","Operational Disclaimer","Termination",
            "Limitation of Liability","Indemnification","Governing Law","Changes to Terms",
            "Third-Party Credits","Contact"].map((s, i) => (
            <li key={i}><a href={`#tos-${i + 1}`}>{s}</a></li>
          ))}
        </ol>
      </div>

      <Section id="tos-1" title="1. Acceptance of Terms">
        <p>By creating an account, accessing, or using Pinch Point (the "Service"), you ("User," "you") agree to be bound by these Terms of Service ("Terms"), our Privacy Policy, and End User License Agreement ("EULA"), each incorporated herein by reference. If you are accepting on behalf of an organization, you represent that you have authority to bind that organization to these Terms.</p>
        <p>If you do not agree to all of these Terms, you may not use the Service.</p>
      </Section>

      <Section id="tos-2" title="2. Description of Service">
        <p>Pinch Point is a cloud-based operational issue-tracking and task management platform operated by {COMPANY}. The Service enables authenticated users within a licensed organization to log operational issues ("Pinch Points"), assign and track corrective tasks, manage vendor contacts, and generate performance analytics.</p>
        <p>The Service is available on a subscription or license basis to organizations across all industries. While initially developed with aviation and ground operations in mind, Pinch Point is designed for any workforce team that requires structured operational reporting.</p>
      </Section>

      <Section id="tos-3" title="3. License Grant">
        <div className="legal-highlight">
          We grant you a personal, non-exclusive, non-transferable, revocable license to access and use the Service solely for your organization's internal business purposes, subject to these Terms.
        </div>
        <p>This license does not include the right to:</p>
        <ul>
          <li>Sell, lease, sublicense, or otherwise transfer access to the Service or any portion thereof to any third party;</li>
          <li>Reproduce, copy, or create derivative works based on the Service or its content;</li>
          <li>Use the Service to build, operate, or market a competing product or service;</li>
          <li>Remove, obscure, or alter any proprietary notices, labels, or marks in the Service.</li>
        </ul>
      </Section>

      <Section id="tos-4" title="4. Proprietary Rights & Intellectual Property">
        <div className="legal-highlight">
          Pinch Point, including its source code, logic, algorithms, data models, user interface design, branding, and documentation, is the exclusive intellectual property of {COMPANY}. All rights not expressly granted in these Terms are reserved.
        </div>
        <p>The Pinch Point name, logo, and all related product marks are trademarks of {COMPANY}. You may not use our trademarks without prior written consent.</p>
        <p>Any unauthorized copying, distribution, modification, public display, public performance, or reverse engineering of the Service is strictly prohibited and may subject you to civil and criminal liability under applicable intellectual property laws.</p>
        <p>If you provide feedback, suggestions, or ideas about the Service ("Feedback"), you grant us a royalty-free, worldwide, perpetual license to use that Feedback without obligation to you.</p>
      </Section>

      <Section id="tos-5" title="5. Prohibited Conduct">
        <p>You agree not to:</p>
        <ul>
          <li><strong>Reverse engineer</strong> or attempt to derive source code, underlying ideas, or algorithms from the Service through decompilation, disassembly, or any other means;</li>
          <li><strong>Automate access</strong> to the Service using bots, scrapers, crawlers, or other automated tools without our prior written consent;</li>
          <li><strong>Attempt unauthorized access</strong> to any portion of the Service, its servers, databases, or related infrastructure;</li>
          <li><strong>Submit harmful content</strong> including false, defamatory, harassing, obscene, or illegal material;</li>
          <li><strong>Impersonate</strong> any person, entity, or organization;</li>
          <li><strong>Interfere</strong> with or disrupt the Service or servers connected to the Service;</li>
          <li>Use the Service in violation of any applicable local, state, national, or international law.</li>
        </ul>
      </Section>

      <Section id="tos-6" title="6. User Content">
        <p>You retain ownership of the operational content ("Posts," "Tasks," "Comments") you submit through the Service. By submitting content, you grant {COMPANY} a non-exclusive, worldwide, royalty-free license to store, process, and display that content solely to the extent necessary to operate and provide the Service to your organization.</p>
        <p>You represent and warrant that: (a) you own or have the necessary rights to the content you submit; (b) your content does not violate any third-party intellectual property rights, privacy rights, or applicable laws; and (c) your content is accurate and submitted in good faith for legitimate operational purposes.</p>
        <p>We reserve the right to remove content that violates these Terms or applicable law.</p>
      </Section>

      <Section id="tos-7" title="7. Operational Disclaimer">
        <div className="legal-warning">
          The data, metrics, and analytics provided by the Service are for <strong>informational and operational planning purposes only</strong>. Users are solely responsible for independently verifying the accuracy of all data and analytics before making operational, personnel, safety, or financial decisions.
        </div>
        <p>The Service does not constitute professional safety consulting, regulatory compliance, legal, or financial advice. {COMPANY} makes no representations or warranties about the accuracy, completeness, or fitness of any operational data submitted by users or generated by AI features.</p>
        <p>AI-assisted features (post rewrites, task suggestions) are tools to assist users and are not a substitute for professional judgment. Always verify AI-generated content before acting on it.</p>
      </Section>

      <Section id="tos-8" title="8. Termination">
        <p>We reserve the right, in our sole discretion, to suspend or permanently terminate any account that:</p>
        <ul>
          <li>Violates these Terms, the Privacy Policy, or the EULA;</li>
          <li>Misuses the Service in ways that harm other users, the platform, or third parties;</li>
          <li>Submits content intended to harass, defame, or harm individuals;</li>
          <li>Attempts unauthorized access to system infrastructure or other organizations' data;</li>
          <li>Engages in activity that creates legal liability for {COMPANY}.</li>
        </ul>
        <p>You may terminate your account at any time by contacting us. Upon termination (by either party), your license to use the Service immediately ends. Termination does not entitle you to a refund of any fees paid.</p>
      </Section>

      <Section id="tos-9" title="9. Limitation of Liability">
        <div className="legal-warning">
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL {COMPANY.toUpperCase()}, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO: (a) OPERATIONAL LOSSES; (b) FINANCIAL OR ECONOMIC DAMAGES; (c) LOSS OF DATA, REVENUE, OR PROFIT; (d) BUSINESS INTERRUPTION—ARISING FROM YOUR USE OF OR INABILITY TO USE THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </div>
        <p>Our total aggregate liability to you for all claims arising under or related to these Terms shall not exceed the greater of (a) the amounts paid by you to us in the twelve (12) months immediately preceding the claim, or (b) one hundred U.S. dollars ($100.00).</p>
        <p>Some jurisdictions do not allow exclusion of certain warranties or limitations of liability. In those jurisdictions, our liability is limited to the greatest extent permitted by law.</p>
      </Section>

      <Section id="tos-10" title="10. Indemnification">
        <p>You agree to indemnify, defend, and hold harmless {COMPANY} and its officers, directors, employees, and agents from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to: (a) your use of the Service; (b) your violation of these Terms; (c) your violation of any rights of a third party; or (d) your User Content.</p>
      </Section>

      <Section id="tos-11" title="11. Governing Law & Dispute Resolution">
        <p>These Terms are governed by the laws of the State of {CONTACT_STATE}, without regard to its conflict-of-law provisions.</p>
        <p><strong>Arbitration:</strong> Any dispute, claim, or controversy arising out of or relating to these Terms or the Service that cannot be resolved by mutual agreement shall be submitted to binding arbitration administered by JAMS under its then-current Commercial Arbitration Rules. Arbitration will be conducted in {CONTACT_STATE}. The arbitrator's decision shall be final and binding.</p>
        <p><strong>Class Action Waiver:</strong> You waive any right to participate in a class action lawsuit or class-wide arbitration against {COMPANY}.</p>
        <p><strong>Exception:</strong> Either party may seek injunctive or other equitable relief in a court of competent jurisdiction to prevent actual or threatened infringement, misappropriation, or violation of intellectual property rights.</p>
      </Section>

      <Section id="tos-12" title="12. Changes to Terms">
        <p>We may update these Terms at any time. When material changes are made, we will notify registered users by email at least 14 days before the changes take effect, using the email address on file. The notification will identify the nature of the change and the new effective date.</p>
        <p>Your continued use of the Service after the effective date of updated Terms constitutes acceptance. If you do not accept the updated Terms, you must stop using the Service and may request account deletion.</p>
        <p>A version history of these Terms is maintained at <a href="/terms">/terms</a>. The version you agreed to at registration is stored in your account profile.</p>
      </Section>

      <Section id="tos-13" title="13. Third-Party Credits">
        <p>Pinch Point is built using the following open-source and third-party software. We gratefully acknowledge the work of their contributors:</p>
        <table className="legal-credits-table">
          <thead>
            <tr><th>Package</th><th>Version</th><th>License</th><th>Author / Owner</th></tr>
          </thead>
          <tbody>
            {[
              ["React", "18.3.1", "MIT", "Meta Platforms, Inc."],
              ["React DOM", "18.3.1", "MIT", "Meta Platforms, Inc."],
              ["Firebase SDK", "10.12.2", "Apache 2.0", "Google LLC"],
              ["Recharts", "2.12.7", "MIT", "Recharts Group"],
              ["date-fns", "3.6.0", "MIT", "date-fns contributors"],
              ["Create React App", "5.0.1", "MIT", "Meta Platforms, Inc."],
            ].map(([pkg, ver, lic, author]) => (
              <tr key={pkg}>
                <td><strong>{pkg}</strong></td>
                <td>{ver}</td>
                <td>{lic}</td>
                <td>{author}</td>
              </tr>
            ))}
            <tr>
              <td><strong>Claude API</strong></td>
              <td>—</td>
              <td>
                <a href="https://www.anthropic.com/legal/aup" target="_blank" rel="noopener noreferrer">API Terms of Service</a>
                {" · "}
                <a href="https://www.anthropic.com/legal/usage-policy" target="_blank" rel="noopener noreferrer">Usage Policy</a>
              </td>
              <td>Anthropic PBC</td>
            </tr>
          </tbody>
        </table>
        <p style={{ marginTop: 12, fontSize: 13, color: "var(--text-dim)" }}>
          Use of these packages is subject to their respective licenses. Inclusion does not imply endorsement by their authors of Pinch Point or {COMPANY}.
        </p>
      </Section>

      <Section id="tos-14" title="14. Contact">
        <p>For legal inquiries, Terms questions, or to report a violation:</p>
        <div className="legal-highlight">
          <strong>{COMPANY}</strong><br />
          Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </div>
      </Section>
    </LegalShell>
  );
}

// ── Privacy Policy ─────────────────────────────────────────────────────────────
function PrivacyContent() {
  return (
    <LegalShell title="Privacy Policy" version={TERMS_VERSION} effectiveDate={TERMS_EFFECTIVE_DATE}>
      <div className="legal-toc">
        <div className="legal-toc-title">Table of Contents</div>
        <ol>
          {["Who We Are","Information We Collect","How We Use Your Information",
            "Cookies & Tracking Technologies","Third-Party Services","Data Retention",
            "Security","Your Rights (GDPR / CCPA / CPRA)","Children's Privacy",
            "Policy Updates","Contact & Data Requests"].map((s, i) => (
            <li key={i}><a href={`#pp-${i + 1}`}>{s}</a></li>
          ))}
        </ol>
      </div>

      <Section id="pp-1" title="1. Who We Are">
        <p>Pinch Point is operated by {COMPANY} ("Company," "we," "us," "our"). This Privacy Policy describes how we collect, use, disclose, and protect your personal information when you use our Service.</p>
        <p>For the purposes of GDPR, {COMPANY} is the data controller for information collected through the Service.</p>
      </Section>

      <Section id="pp-2" title="2. Information We Collect">
        <p><strong>Information you provide directly:</strong></p>
        <ul>
          <li><strong>Account information:</strong> Full name, email address, display name, and password (stored as a salted hash by Firebase Authentication — we never see your raw password).</li>
          <li><strong>Organizational content:</strong> Operational posts ("Pinch Points"), task descriptions, comments, vendor contact information, and other content you submit through the Service.</li>
          <li><strong>Profile data:</strong> Your role (user, admin, or superuser), organization membership, and account settings.</li>
        </ul>
        <p><strong>Information collected automatically:</strong></p>
        <ul>
          <li><strong>User identifiers:</strong> Firebase Authentication UID assigned at account creation.</li>
          <li><strong>Network data:</strong> IP address and approximate geographic region (country/region level) captured by Firebase at connection time.</li>
          <li><strong>Device & browser information:</strong> Browser type, operating system, and device type collected for security and compatibility purposes.</li>
          <li><strong>Usage logs:</strong> Login/logout timestamps, feature interaction logs captured by Firebase.</li>
        </ul>
        <p><strong>We do not collect:</strong> Social Security numbers, financial account numbers, payment card information, precise geolocation, or health/biometric information.</p>
      </Section>

      <Section id="pp-3" title="3. How We Use Your Information">
        <p>We use your information for the following purposes:</p>
        <ul>
          <li>To provide, operate, and maintain the Service and your account;</li>
          <li>To authenticate your identity and manage your session securely;</li>
          <li>To display your name, role, and activity within your organization's workspace;</li>
          <li>To facilitate task assignment, post management, and operational analytics;</li>
          <li>To power AI-assisted features (post rewrites, suggestions) — content is processed by Anthropic's Claude API and is not stored by us beyond the request;</li>
          <li>To send important service communications, including Terms-of-Service update notices;</li>
          <li>To investigate and respond to security incidents or abuse reports;</li>
          <li>To improve Service performance, reliability, and user experience;</li>
          <li>To comply with applicable legal obligations.</li>
        </ul>
        <p>We do not sell your personal information. We do not use your personal information for advertising or behavioral profiling.</p>
      </Section>

      <Section id="pp-4" title="4. Cookies & Tracking Technologies">
        <p>The Service uses cookies and local storage exclusively for <strong>essential authentication and session management</strong> through Firebase Authentication. These cookies are required for the Service to function and cannot be disabled without breaking your ability to log in.</p>
        <p>We do not use advertising cookies, third-party tracking pixels, behavioral retargeting, or analytics tools that track individuals across websites.</p>
        <p>Firebase may set the following types of storage:</p>
        <ul>
          <li><strong>Firebase Auth session cookies:</strong> Required to maintain your authenticated session.</li>
          <li><strong>Local Storage:</strong> Used to store your cookie consent preference and display preferences (e.g., dark mode).</li>
        </ul>
        <p>For more information on Firebase's cookie practices, see <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.</p>
      </Section>

      <Section id="pp-5" title="5. Third-Party Services">
        <p>We share data with the following third-party service providers solely to operate the Service:</p>
        <table className="legal-credits-table">
          <thead>
            <tr><th>Processor</th><th>Purpose</th><th>Data Shared</th><th>Privacy Policy</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Google LLC (Firebase)</strong></td>
              <td>Authentication, Firestore database, Cloud Functions, Hosting</td>
              <td>Account data, posts, tasks, IP address, session tokens</td>
              <td><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a></td>
            </tr>
            <tr>
              <td><strong>Anthropic PBC</strong></td>
              <td>AI-assisted content features (Claude API)</td>
              <td>Post text submitted for rewrite or moderation (no PII)</td>
              <td><a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer">anthropic.com/privacy</a></td>
            </tr>
          </tbody>
        </table>
        <p>Your data processed by Firebase is governed by Google's Data Processing Terms and stored in secure, Google-operated data centers. Anthropic processes submitted content through their API under their usage policies; we do not send personally identifying information to Anthropic.</p>
      </Section>

      <Section id="pp-6" title="6. Data Retention">
        <p>We retain your account and operational data for as long as your account is active and for a reasonable period thereafter to fulfill the purposes described in this Policy.</p>
        <ul>
          <li><strong>Active accounts:</strong> Data is retained for the duration of the account's active status.</li>
          <li><strong>Deleted accounts:</strong> Upon verified deletion request, we will purge your personal data from active databases within 30 days. Backup archives may retain data for up to 90 additional days before automatic purge.</li>
          <li><strong>Operational content:</strong> Posts, tasks, and analytics data are retained as long as your organization's account is active, as they are required for historical analysis and operational continuity.</li>
          <li><strong>Legal hold:</strong> Data required for ongoing legal proceedings or regulatory compliance may be retained beyond the above periods as required by law.</li>
        </ul>
      </Section>

      <Section id="pp-7" title="7. Security">
        <p>We implement appropriate technical and organizational safeguards to protect your personal information, including:</p>
        <ul>
          <li>Encrypted data transmission using TLS 1.2+ (HTTPS) for all data in transit;</li>
          <li>AES-256 encryption for data at rest, managed by Google Cloud;</li>
          <li>Role-based access controls within each organization;</li>
          <li>Firestore security rules that enforce strict organizational data isolation;</li>
          <li>Firebase Authentication for secure, token-based session management.</li>
        </ul>
        <p>See our <a href="/security">Data Security page</a> for a detailed technical overview.</p>
        <p>While we implement strong safeguards, no internet transmission or electronic storage is 100% secure. We cannot guarantee absolute security of your data.</p>
      </Section>

      <Section id="pp-8" title="8. Your Rights (GDPR / CCPA / CPRA)">
        <p><strong>EU/EEA Users (GDPR):</strong> You have the right to:</p>
        <ul>
          <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
          <li><strong>Rectification:</strong> Request correction of inaccurate personal data.</li>
          <li><strong>Erasure:</strong> Request deletion of your personal data ("right to be forgotten").</li>
          <li><strong>Restriction:</strong> Request that we restrict processing of your data in certain circumstances.</li>
          <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format.</li>
          <li><strong>Object:</strong> Object to processing based on legitimate interests.</li>
          <li><strong>Withdraw consent:</strong> Where processing is based on consent, withdraw it at any time.</li>
          <li><strong>Lodge a complaint</strong> with your local data protection supervisory authority.</li>
        </ul>
        <p><strong>California Users (CCPA / CPRA):</strong> You have the right to:</p>
        <ul>
          <li><strong>Know:</strong> What personal information we collect, use, disclose, and sell.</li>
          <li><strong>Delete:</strong> Request deletion of personal information we have collected.</li>
          <li><strong>Correct:</strong> Request correction of inaccurate personal information.</li>
          <li><strong>Opt-Out of Sale/Sharing:</strong> We do not sell or share personal information for cross-context behavioral advertising.</li>
          <li><strong>Limit Use of Sensitive Personal Information:</strong> See our <a href="/data-choices">Data Choices page</a>.</li>
          <li><strong>Non-Discrimination:</strong> You will not be denied service or discriminated against for exercising your CCPA rights.</li>
        </ul>
        <p>To exercise any of these rights, submit a verifiable request to <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We will respond within 45 days (extendable by 45 additional days with notice).</p>
      </Section>

      <Section id="pp-9" title="9. Children's Privacy">
        <p>The Service is not directed at, and we do not knowingly collect personal information from, children under the age of 13 (or under 16 for users in the EU/EEA). If we become aware that we have inadvertently collected personal information from a child below the applicable age threshold, we will take steps to delete it promptly.</p>
        <p>If you believe a child has provided personal information to us, please contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
      </Section>

      <Section id="pp-10" title="10. Policy Updates">
        <p>We may update this Privacy Policy periodically. When we make material changes, we will notify registered users by email at least 14 days before the changes take effect. The updated Policy will be accessible at <a href="/privacy">/privacy</a>. Your continued use of the Service after the effective date constitutes acceptance.</p>
      </Section>

      <Section id="pp-11" title="11. Contact & Data Requests">
        <p>For privacy-related inquiries, data access requests, deletion requests, or to report a privacy concern:</p>
        <div className="legal-highlight">
          <strong>Data Controller: {COMPANY}</strong><br />
          Privacy Contact: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a><br />
          Subject line: "Privacy Request – [Your Name]"
        </div>
        <p>Please include your full name, email address on file, and a description of your request. We may need to verify your identity before fulfilling the request.</p>
      </Section>
    </LegalShell>
  );
}

// ── EULA ───────────────────────────────────────────────────────────────────────
function EULAContent() {
  return (
    <LegalShell title="End User License Agreement (EULA)" version={TERMS_VERSION} effectiveDate={TERMS_EFFECTIVE_DATE}>
      <p style={{ fontSize: 14, color: "var(--text-dim)", marginBottom: 24 }}>
        This End User License Agreement ("EULA") is a legally binding agreement between you and {COMPANY} governing your use of the Pinch Point software. By installing, accessing, or using the software, you agree to be bound by this EULA.
      </p>

      <Section id="eula-1" title="1. Grant of License">
        <p>{COMPANY} ("Licensor") grants you ("Licensee") a limited, non-exclusive, non-transferable, revocable license to access and use Pinch Point ("Software") solely for your organization's internal business operations, subject to the terms of this EULA.</p>
        <p>This license is personal to you and the organization under which you are registered. It does not extend to any affiliated entities without a separate written agreement.</p>
      </Section>

      <Section id="eula-2" title="2. Restrictions">
        <p>You may not, directly or indirectly:</p>
        <ul>
          <li><strong>Copy or reproduce</strong> the Software or any portion thereof;</li>
          <li><strong>Modify, adapt, or create derivative works</strong> based on the Software;</li>
          <li><strong>Distribute, publish, sell, sublicense, rent, lease, or lend</strong> the Software to any third party;</li>
          <li><strong>Reverse engineer, decompile, disassemble</strong>, or otherwise attempt to derive the source code, underlying algorithms, or structural design of the Software;</li>
          <li><strong>Remove, alter, or obscure</strong> any proprietary notices, labels, or marks on the Software;</li>
          <li><strong>Use the Software for competitive analysis</strong> or to develop competing products without prior written consent from {COMPANY};</li>
          <li><strong>Transfer</strong> your license rights to any other person or entity.</li>
        </ul>
        <div className="legal-warning">
          Reverse engineering and unauthorized copying of Pinch Point are strictly prohibited and constitute violations of intellectual property law. {COMPANY} reserves the right to pursue all available legal remedies, including injunctive relief and monetary damages.
        </div>
      </Section>

      <Section id="eula-3" title="3. Intellectual Property Ownership">
        <div className="legal-highlight">
          Pinch Point, including its source code, logic, algorithms, data models, user interface design, documentation, and all associated materials, is the exclusive property of {COMPANY}. All rights, title, and interest in and to the Software — including all intellectual property rights — are and shall remain exclusively with {COMPANY}.
        </div>
        <p>Nothing in this EULA transfers ownership of the Software or any intellectual property rights therein to you. The Software is licensed, not sold.</p>
      </Section>

      <Section id="eula-4" title='4. "As-Is" Disclaimer'>
        <div className="legal-warning">
          THE SOFTWARE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, {COMPANY.toUpperCase()} EXPRESSLY DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO:
          <ul style={{ marginTop: 8 }}>
            <li>WARRANTIES OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE;</li>
            <li>WARRANTIES THAT THE SOFTWARE WILL BE ERROR-FREE, UNINTERRUPTED, OR SECURE;</li>
            <li>WARRANTIES THAT DEFECTS WILL BE CORRECTED;</li>
            <li>WARRANTIES THAT ANALYTICS CALCULATIONS OR AI-GENERATED CONTENT WILL BE ACCURATE OR RELIABLE.</li>
          </ul>
        </div>
        <p>The entire risk as to the quality and performance of the Software is with you. You are responsible for verifying the accuracy of all data and analytics before making business, operational, safety, or personnel decisions.</p>
      </Section>

      <Section id="eula-5" title="5. Limitation of Liability">
        <div className="legal-warning">
          IN NO EVENT SHALL {COMPANY.toUpperCase()} OR ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF OR INABILITY TO USE THE SOFTWARE, INCLUDING WITHOUT LIMITATION: OPERATIONAL LOSSES, FINANCIAL DAMAGES, LOSS OF DATA, LOSS OF PROFIT, OR BUSINESS INTERRUPTION — EVEN IF {COMPANY.toUpperCase()} HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </div>
        <p>The Licensor's total liability under this EULA for all claims shall not exceed the greater of (a) one hundred U.S. dollars ($100.00) or (b) the amounts paid by you in the twelve months preceding the claim.</p>
      </Section>

      <Section id="eula-6" title="6. Termination">
        <p>This EULA is effective from the date you first access the Software and continues until terminated.</p>
        <p>This EULA terminates automatically and without notice if you breach any provision herein. {COMPANY} may also terminate this EULA at any time for any reason upon notice.</p>
        <p>Upon termination: (a) all license rights granted herein immediately cease; (b) you must stop all use of the Software; (c) any provisions of this EULA that by their nature should survive termination shall survive, including ownership, disclaimer, limitation of liability, and governing law sections.</p>
      </Section>

      <Section id="eula-7" title="7. Governing Law">
        <p>This EULA is governed by the laws of the State of {CONTACT_STATE}, without regard to conflict-of-law principles. Any disputes shall be resolved by binding arbitration as described in the Terms of Service.</p>
      </Section>

      <Section id="eula-8" title="8. Entire Agreement">
        <p>This EULA, together with the Terms of Service and Privacy Policy, constitutes the entire agreement between you and {COMPANY} with respect to the Software and supersedes all prior agreements, representations, and understandings. If any provision of this EULA is found unenforceable, the remaining provisions remain in full force.</p>
      </Section>
    </LegalShell>
  );
}

// ── Data Security ──────────────────────────────────────────────────────────────
function SecurityContent() {
  return (
    <LegalShell title="Data Security" version={TERMS_VERSION} effectiveDate={TERMS_EFFECTIVE_DATE}>
      <p style={{ fontSize: 14, color: "var(--text-dim)", marginBottom: 24 }}>
        This page outlines the technical and organizational security measures {COMPANY} employs to protect your data within the Pinch Point Service.
      </p>

      <Section id="sec-1" title="1. Infrastructure & Hosting">
        <p>Pinch Point is hosted entirely on <strong>Google Firebase</strong>, a platform operated by Google LLC. Firebase infrastructure maintains comprehensive security certifications:</p>
        <ul>
          <li>ISO/IEC 27001:2013 (Information Security Management)</li>
          <li>SOC 2 Type II</li>
          <li>SOC 3</li>
          <li>FedRAMP Authorized</li>
          <li>PCI DSS Level 1</li>
        </ul>
        <p>Firebase operates across Google's global network of geographically redundant, physically secured data centers. For the most current compliance details, see <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">Firebase's privacy documentation</a>.</p>
      </Section>

      <Section id="sec-2" title="2. Encryption">
        <p><strong>Data in transit:</strong> All communications between your browser and our servers are encrypted using TLS 1.2 or higher (HTTPS). Connections using outdated protocols are rejected.</p>
        <p><strong>Data at rest:</strong> All data stored in Firebase Firestore, Firebase Authentication, and Cloud Storage is encrypted at rest using AES-256 encryption, managed by Google Cloud's Key Management Service (KMS).</p>
      </Section>

      <Section id="sec-3" title="3. Authentication & Access Control">
        <div className="legal-highlight">
          <strong>Firebase Authentication</strong> handles all user sessions using industry-standard OAuth 2.0 / JWT token-based authentication. We never store or transmit raw passwords — credentials are salted, hashed, and managed entirely by Firebase.
        </div>
        <p><strong>Role-based access control (RBAC)</strong> limits data access within organizations:</p>
        <ul>
          <li><strong>User:</strong> Can submit posts and view their assigned tasks within their organization;</li>
          <li><strong>Admin:</strong> Full access to their organization's data, users, and settings;</li>
          <li><strong>Super User:</strong> Admin privileges plus the ability to manage roles.</li>
        </ul>
        <p>Administrative functions are protected by role checks enforced at both the application and Firestore security rule layers.</p>
      </Section>

      <Section id="sec-4" title="4. Organizational Data Isolation">
        <p>Each organization's data is stored in isolated Firestore subcollections using path-level isolation:</p>
        <div className="legal-highlight">
          <code>/organizations/{'{orgId}'}/pinchpoints</code><br />
          <code>/organizations/{'{orgId}'}/tasks</code><br />
          <code>/organizations/{'{orgId}'}/members</code><br />
          <code>/organizations/{'{orgId}'}/vendors</code>
        </div>
        <p>Firestore security rules enforce strict org-level boundaries at the database level — authenticated users can only read or write data within their own organization's subcollections. Cross-organizational data access is architecturally impossible through normal usage.</p>
      </Section>

      <Section id="sec-5" title="5. AI Feature Data Handling">
        <p>When you use AI-powered features (post rewrites, task suggestions, content moderation), the relevant text content is transmitted over encrypted HTTPS to <strong>Anthropic's Claude API</strong>. This processing is governed by Anthropic's usage policies and data processing agreements.</p>
        <p>We do not send personally identifying information (names, emails, UIDs) to the AI API — only the operational text content you have submitted. AI API responses are processed in real-time and are not persisted beyond the user's current session.</p>
      </Section>

      <Section id="sec-6" title="6. Incident Response">
        <p>In the event of a confirmed data security incident affecting personal information:</p>
        <ul>
          <li>We will notify affected users and relevant regulatory authorities within 72 hours of discovery, as required by applicable law;</li>
          <li>Notifications will be sent to the email addresses on file;</li>
          <li>We will provide a description of the nature of the breach, data categories affected, and steps taken to mitigate harm.</li>
        </ul>
        <p>To report a suspected security vulnerability, contact <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> with subject line "Security Disclosure."</p>
      </Section>

      <Section id="sec-7" title="7. Sub-processors">
        <table className="legal-credits-table">
          <thead>
            <tr><th>Processor</th><th>Role</th><th>Data Location</th><th>Certifications</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Google LLC (Firebase)</strong></td>
              <td>Database, Authentication, Cloud Functions, Hosting</td>
              <td>United States (primary), global redundancy</td>
              <td>ISO 27001, SOC 2, FedRAMP</td>
            </tr>
            <tr>
              <td><strong>Anthropic PBC</strong></td>
              <td>AI text processing (Claude API)</td>
              <td>United States</td>
              <td>SOC 2 Type II (in progress)</td>
            </tr>
          </tbody>
        </table>
      </Section>
    </LegalShell>
  );
}

// ── Data Choices (CPRA) ────────────────────────────────────────────────────────
function DataChoicesContent() {
  return (
    <LegalShell title="Limit Use of My Data" version={TERMS_VERSION} effectiveDate={TERMS_EFFECTIVE_DATE}>
      <p style={{ fontSize: 14, color: "var(--text-dim)", marginBottom: 24 }}>
        This page describes your rights under the California Privacy Rights Act (CPRA), California Consumer Privacy Act (CCPA), and other applicable privacy laws to control how your personal information is used.
      </p>

      <Section id="dc-1" title="1. Overview">
        <p>{COMPANY} is committed to transparency about the personal information we collect and your rights to control its use. This page is provided in compliance with the California Privacy Rights Act (CPRA), effective January 1, 2023, and applies to California residents. Users in other jurisdictions may also exercise similar rights under applicable local laws.</p>
        <p><strong>We do not sell personal information.</strong> We do not share personal information for cross-context behavioral advertising. Your data is used solely to operate the Pinch Point Service.</p>
      </Section>

      <Section id="dc-2" title="2. Personal Information We Collect and Why">
        <table className="legal-credits-table">
          <thead>
            <tr><th>Category</th><th>Specific Data</th><th>Business Purpose</th><th>Shared With</th></tr>
          </thead>
          <tbody>
            {[
              ["Identifiers", "Email address, display name, Firebase UID", "Account creation & authentication", "Firebase/Google"],
              ["Internet Activity", "IP address, session logs, browser type", "Security, fraud prevention", "Firebase/Google"],
              ["Professional Data", "Role, organization membership, operational posts submitted", "Core service functionality", "Firebase/Google"],
              ["AI-Processed Content", "Post text submitted for AI features", "Content assistance & moderation", "Anthropic PBC"],
            ].map(([cat, data, purpose, shared]) => (
              <tr key={cat}>
                <td><strong>{cat}</strong></td>
                <td>{data}</td>
                <td>{purpose}</td>
                <td>{shared}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section id="dc-3" title="3. Do Not Sell or Share My Personal Information">
        <div className="legal-highlight">
          {COMPANY} does not sell your personal information to third parties, and does not share your personal information for cross-context behavioral advertising. This section is provided for transparency; there is no opt-out required because the practice does not occur.
        </div>
        <p>If this practice changes, we will update this page, notify users by email, and provide a prominent "Do Not Sell or Share My Personal Information" link at least 30 days before such changes take effect.</p>
      </Section>

      <Section id="dc-4" title="4. Right to Know">
        <p>You have the right to request that we disclose:</p>
        <ul>
          <li>The specific pieces of personal information we have collected about you;</li>
          <li>The categories of personal information collected;</li>
          <li>The categories of sources from which personal information is collected;</li>
          <li>Our business or commercial purpose for collecting personal information;</li>
          <li>The categories of third parties with whom we share personal information.</li>
        </ul>
        <p>You may submit up to two (2) disclosure requests per 12-month period.</p>
      </Section>

      <Section id="dc-5" title="5. Right to Delete">
        <p>You have the right to request deletion of personal information we have collected about you, subject to certain exceptions allowed by law (e.g., data needed to complete a transaction, detect security incidents, or comply with legal obligations).</p>
        <p>Upon a verified deletion request, we will:</p>
        <ul>
          <li>Delete your account and personal data from active systems within 30 days;</li>
          <li>Direct our service providers (Firebase, Anthropic) to delete your data where applicable;</li>
          <li>Confirm completion of the deletion to you in writing.</li>
        </ul>
        <p>Note: Operational posts and tasks may be retained in aggregate, de-identified form for historical analytics after personal identifiers are removed.</p>
      </Section>

      <Section id="dc-6" title="6. Right to Correct">
        <p>You have the right to request correction of inaccurate personal information we hold about you. To request a correction, contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> with the specific information you believe is incorrect and the correct value. We will review and respond within 45 days.</p>
      </Section>

      <Section id="dc-7" title="7. Right to Limit Use of Sensitive Personal Information">
        <p>Under CPRA, you have the right to direct businesses to limit the use of "sensitive personal information" to that which is necessary to perform the services. The personal information we collect does not include CPRA-defined sensitive personal information categories (such as Social Security numbers, financial account details, precise geolocation, racial/ethnic origin, or health information).</p>
        <p>If this changes, we will update this page and provide a clear mechanism to exercise this right.</p>
      </Section>

      <Section id="dc-8" title="8. How to Submit a Privacy Request">
        <div className="legal-highlight">
          <strong>Submit a request by email:</strong><br />
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a><br />
          Subject line: "California Privacy Request – [Right to Know / Delete / Correct / Opt-Out]"<br /><br />
          Include: Your full name, email address on your account, the specific right you are exercising, and any additional details relevant to your request.
        </div>
        <p>We will acknowledge receipt within 10 business days and fulfill verified requests within 45 calendar days (extendable by 45 additional days with prior notice).</p>
      </Section>

      <Section id="dc-9" title="9. Verification">
        <p>To protect your privacy, we must verify your identity before fulfilling a data request. For requests submitted by email, we will verify by sending a confirmation to the email address on your account. For sensitive requests (deletion), we may require additional verification.</p>
        <p>We cannot fulfill a request we cannot verify. Unverified requests will be declined with an explanation.</p>
      </Section>

      <Section id="dc-10" title="10. Authorized Agents">
        <p>A California resident may designate an authorized agent to submit a request on their behalf. Authorized agents must provide written authorization signed by you, and we may require direct verification from you before processing the request.</p>
      </Section>

      <Section id="dc-11" title="11. Non-Discrimination">
        <p>We will not discriminate against you for exercising any of your CCPA/CPRA rights. We will not deny you goods or services, charge different prices, provide a different level of service, or suggest you will receive worse treatment for exercising your privacy rights.</p>
      </Section>
    </LegalShell>
  );
}

// ── Shared section component ───────────────────────────────────────────────────
function Section({ id, title, children }) {
  return (
    <section id={id} className="legal-section">
      <h2 className="legal-section-title">{title}</h2>
      {children}
    </section>
  );
}

// ── Router ─────────────────────────────────────────────────────────────────────
export default function LegalPage() {
  const path = window.location.pathname;
  const pages = {
    "/terms": TermsContent,
    "/privacy": PrivacyContent,
    "/eula": EULAContent,
    "/security": SecurityContent,
    "/data-choices": DataChoicesContent,
  };
  const PageComponent = pages[path];
  if (!PageComponent) {
    window.location.href = "/";
    return null;
  }
  return <PageComponent />;
}
